import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from 'react';
import {
  Container,
  Card,
  Button,
  Modal,
  Form,
  Row,
  Col,
  InputGroup,
  Spinner,
} from 'react-bootstrap';
import Select from 'react-select';
import axios from 'axios';
import BASE_URL from '../config';
import { FaEdit, FaTrashAlt, FaFileExcel, FaBoxOpen } from 'react-icons/fa';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import DataTable from 'react-data-table-component';
import './AdminLayout.css';

// Axios instance with interceptor for authentication
const authAxios = axios.create({
  baseURL: BASE_URL,
});

authAxios.interceptors.request.use((config) => {
  const token = localStorage.getItem('authToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Admin Driver Creation/Edit Modal Component
const AdminDriverCreation = ({
  show,
  onClose,
  onDriverCreated,
  driverToEdit,
}) => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    password: '',
    showrooms: [],
    is_active: false,
  });
  const [showroomOptions, setShowroomOptions] = useState([]);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  // Message modal states
  const [messageModal, setMessageModal] = useState({
    show: false,
    title: '',
    body: '',
  });

  useEffect(() => {
    const fetchAllShowrooms = async () => {
      try {
        const res = await authAxios.get(`/showrooms`);
        const data = res.data?.data?.data || res.data?.data || [];
        setShowroomOptions(
          data.map((s) => ({
            value: s.id,
            label: s.name,
          }))
        );
      } catch (err) {
        console.error('❌ Error fetching showrooms:', err);
      }
    };
    fetchAllShowrooms();
  }, []);

  useEffect(() => {
    if (show && !messageModal.show) {
      // reset only when modal is shown and message modal is not visible
      if (driverToEdit) {
        setFormData({
          name: driverToEdit.name || '',
          phone: driverToEdit.phone || '',
          email: driverToEdit.email || '',
          password: '',
          showrooms: (driverToEdit.showrooms || []).map((s) => ({
            value: s.id,
            label: s.name,
          })),
          is_active: driverToEdit.is_active || false,
        });
      } else {
        setFormData({
          name: '',
          phone: '',
          email: '',
          password: '',
          showrooms: [],
          is_active: false,
        });
      }
      setErrors({});
    }
  }, [show, driverToEdit, messageModal.show]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  const handleCheckboxChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      is_active: e.target.checked,
    }));
  };

  const handleShowroomChange = (selectedOptions) => {
    setFormData((prev) => ({
      ...prev,
      showrooms: selectedOptions || [],
    }));
    if (errors.showroom_id) {
      setErrors((prev) => ({ ...prev, showroom_id: null }));
    }
  };
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrors({});

    const showroomIds = formData.showrooms.map((s) => s.value);

    const payload = {
      name: formData.name,
      email: formData.email,
      phone: formData.phone,
      showroom_id: showroomIds,
      is_active: formData.is_active,
      role: 'driver',
    };

    if (formData.password) payload.password = formData.password;

    try {
      if (driverToEdit) {
        await authAxios.put(`/users/${driverToEdit.id}`, payload);
        setMessageModal({
          show: true,
          title: 'Success',
          body: 'Driver updated successfully! 🎉',
        });
      } else {
        await authAxios.post('/users', payload);
        setMessageModal({
          show: true,
          title: 'Success',
          body: 'Driver created successfully! 🎉',
        });
      }

      onDriverCreated(); // refresh drivers
    } catch (err) {
      console.error('❌ Error submitting driver form:', err);

      // Handle password error specifically
      if (err.response?.data?.errors?.password) {
        setErrors({ password: ['Password invalid'] });
        setMessageModal({
          show: true,
          title: 'Error',
          body: 'Password invalid. Please enter a valid password.',
        });
      } else {
        const errorMsg =
          err.response?.data?.message ||
          err.response?.data?.error ||
          'Driver submission failed: Network or unknown error.';

        setErrors(err.response?.data?.errors || {});
        setMessageModal({ show: true, title: 'Error', body: errorMsg });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Modal
        show={show}
        onHide={() => !messageModal.show && onClose()}
        size='lg'
      >
        <Modal.Header closeButton={!messageModal.show}>
          <Modal.Title>
            {driverToEdit ? 'Edit Driver' : 'Create New Driver'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleSubmit}>
            <Row>
              <Col md={6}>
                <Form.Group controlId='name' className='mb-3'>
                  <Form.Label>Name</Form.Label>
                  <Form.Control
                    type='text'
                    name='name'
                    value={formData.name}
                    onChange={handleChange}
                    placeholder='Enter name'
                    isInvalid={!!errors.name}
                    required
                  />
                  <Form.Control.Feedback type='invalid'>
                    {errors.name?.[0]}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group controlId='phone' className='mb-3'>
                  <Form.Label>Phone</Form.Label>
                  <Form.Control
                    type='text'
                    name='phone'
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder='Enter Phone Number'
                    isInvalid={!!errors.phone}
                    required
                  />
                  <Form.Control.Feedback type='invalid'>
                    {errors.phone?.[0]}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group controlId='email' className='mb-3'>
                  <Form.Label>Email</Form.Label>
                  <Form.Control
                    type='email'
                    name='email'
                    value={formData.email}
                    onChange={handleChange}
                    placeholder='Enter Email'
                    isInvalid={!!errors.email}
                    required
                  />
                  <Form.Control.Feedback type='invalid'>
                    {errors.email?.[0]}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group controlId='password' className='mb-3'>
                  <Form.Label>
                    Password{' '}
                    {driverToEdit && (
                      <small className='text-muted'>
                        (Leave blank to keep existing)
                      </small>
                    )}
                  </Form.Label>
                  <Form.Control
                    type='password'
                    name='password'
                    value={formData.password}
                    onChange={handleChange}
                    placeholder='Enter a password (6 characters min)'
                    isInvalid={!!errors.password}
                    required={!driverToEdit}
                  />
                  <Form.Control.Feedback type='invalid'>
                    {errors.password?.[0]}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>
            <Form.Group controlId='showrooms' className='mb-3'>
              <Form.Label style={{ display: 'block', width: '100%' }}>
                Showrooms
              </Form.Label>
              <Select
                isMulti
                options={showroomOptions}
                onChange={handleShowroomChange}
                placeholder='Select showrooms'
                value={formData.showrooms} // pre-selected showrooms
              />

              {errors.showroom_id && (
                <div
                  className='text-danger mt-1'
                  style={{ fontSize: '0.875em' }}
                >
                  {errors.showroom_id[0]}
                </div>
              )}
            </Form.Group>
            <Form.Group controlId='status' className='mb-3'>
              <Form.Check
                type='checkbox'
                label='Active'
                checked={formData.is_active}
                onChange={handleCheckboxChange}
              />
            </Form.Group>
            <Button
              variant='primary'
              style={{ width: '100%' }}
              type='submit'
              disabled={loading}
            >
              {loading ? 'Submitting...' : driverToEdit ? 'Update' : 'Create'}
            </Button>
          </Form>
        </Modal.Body>
      </Modal>

      {/* Message Modal */}
      <Modal
        show={messageModal.show}
        onHide={() => {
          if (messageModal.title === 'Success') onClose();
          setMessageModal({ show: false, title: '', body: '' });
        }}
        centered
        size='md'
        className='text-center'
      >
        <Modal.Body
          className='py-4'
          style={{
            backgroundColor: '#ffffff',
            color: '#1f1f1f',
            borderRadius: '15px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
          }}
        >
          <div className='mb-3'>
            {messageModal.title === 'Success' ? (
              <svg
                xmlns='http://www.w3.org/2000/svg'
                width='60'
                height='60'
                fill='green'
                className='bi bi-check-circle-fill'
                viewBox='0 0 16 16'
              >
                <path d='M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zM6.97 11.03a.75.75 0 0 0 1.08 0l3.992-3.992a.75.75 0 0 0-1.08-1.04L7.5 9.439 5.53 7.47a.75.75 0 0 0-1.08 1.04l2.52 2.52z' />
              </svg>
            ) : (
              <svg
                xmlns='http://www.w3.org/2000/svg'
                width='60'
                height='60'
                fill='red'
                className='bi bi-x-circle-fill'
                viewBox='0 0 16 16'
              >
                <path d='M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zM4.646 4.646a.5.5 0 0 0 0 .708L7.293 8l-2.647 2.646a.5.5 0 0 0 .708.708L8 8.707l2.646 2.647a.5.5 0 0 0 .708-.708L8.707 8l2.647-2.646a.5.5 0 0 0-.708-.708L8 7.293 5.354 4.646a.5.5 0 0 0-.708 0z' />
              </svg>
            )}
          </div>
          <h5 className='mb-2'>{messageModal.title}</h5>
          <p>{messageModal.body}</p>
          <Button
            variant={messageModal.title === 'Success' ? 'success' : 'danger'}
            style={{
              width: '100%',
              fontWeight: '600',
              borderRadius: '10px',
              background:
                messageModal.title === 'Success'
                  ? 'linear-gradient(135deg, #28a745, #198754)'
                  : 'linear-gradient(135deg, #dc3545, #b02a37)',
              border: 'none',
              boxShadow: '0px 4px 10px rgba(0,0,0,0.25)',
            }}
            onClick={() => {
              if (messageModal.title === 'Success') onClose();
              setMessageModal({ show: false, title: '', body: '' });
            }}
          >
            Close
          </Button>
        </Modal.Body>
      </Modal>
    </>
  );
};

// Main Admin Drivers Component
const AdminDrivers = () => {
  const [drivers, setDrivers] = useState([]);
  const [showrooms, setShowrooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingDriver, setEditingDriver] = useState(null);
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRows, setTotalRows] = useState(0);
  const [perPage, setPerPage] = useState(20);
  const [hasMore, setHasMore] = useState(true);
  const [lastPage, setLastPage] = useState(1);
  const isFetchingRef = useRef(false);

  const fetchShowrooms = async () => {
    try {
      const res = await authAxios.get(`/showrooms`);
      const showroomsData = res.data?.data?.data || res.data?.data || [];
      setShowrooms(Array.isArray(showroomsData) ? showroomsData : []);
    } catch (err) {
      console.error('Failed to fetch showrooms:', err);
      setShowrooms([]);
    }
  };

  // Fetch a single page of drivers
  const fetchDriversPage = async (page, rowsPerPage) => {
    const userShowroomId = localStorage.getItem('userShowroomId');
    const params = {
      // showroom_id: userShowroomId,
      per_page: rowsPerPage,
    };

    const res = await authAxios.get(`/drivers?page=${page}`, { params });

    const responseData = res.data?.data;
    const driversData = Array.isArray(responseData?.data)
      ? responseData.data
      : Array.isArray(responseData)
      ? responseData
      : [];

    // Extract pagination metadata
    const totalCount = responseData?.total;
    const lastPageNum =
      responseData?.last_page ||
      responseData?.meta?.last_page ||
      res.data?.last_page;
    const currentPageNum =
      responseData?.current_page ||
      responseData?.meta?.current_page ||
      res.data?.current_page ||
      page;

    return {
      driversData,
      totalCount,
      lastPageNum,
      currentPageNum,
      hasMore: lastPageNum
        ? page < lastPageNum
        : driversData.length === rowsPerPage,
    };
  };

  // Fetch all drivers page by page until last page
  const fetchAllDriversSequentially = async () => {
    // Prevent multiple simultaneous calls
    if (isFetchingRef.current) {
      return;
    }
    isFetchingRef.current = true;
    setLoading(true);
    setDrivers([]);
    setCurrentPage(1);
    setHasMore(true);

    try {
      let allDriversList = [];
      let page = 1;
      let lastPageNum = null;
      let totalCount = 0;

      // Fetch first page to get metadata
      const firstPageData = await fetchDriversPage(page, perPage);
      allDriversList = [...firstPageData.driversData];
      lastPageNum = firstPageData.lastPageNum;
      totalCount = firstPageData.totalCount || allDriversList.length;

      // Update state with first page data
      setDrivers(allDriversList);
      setCurrentPage(page);
      setTotalRows(totalCount);
      if (lastPageNum) {
        setLastPage(lastPageNum);
      }

      // If we have last_page info, fetch remaining pages
      if (lastPageNum && lastPageNum > 1) {
        for (page = 2; page <= lastPageNum; page++) {
          try {
            const pageData = await fetchDriversPage(page, perPage);
            if (pageData.driversData.length === 0) {
              break;
            }
            allDriversList = [...allDriversList, ...pageData.driversData];
            setDrivers([...allDriversList]);
            setCurrentPage(page);
          } catch (err) {
            console.error(`Error fetching drivers page ${page}:`, err);
            break;
          }
        }
      } else if (firstPageData.hasMore && !lastPageNum) {
        // If we don't have last_page info, continue fetching until we get an empty page
        page = 2;
        while (true) {
          try {
            const pageData = await fetchDriversPage(page, perPage);
            if (pageData.driversData.length === 0) {
              break;
            }
            allDriversList = [...allDriversList, ...pageData.driversData];
            setDrivers([...allDriversList]);
            setCurrentPage(page);

            // If we got fewer items than per_page, it's likely the last page
            if (pageData.driversData.length < perPage) {
              break;
            }
            page++;
          } catch (err) {
            console.error(`Error fetching drivers page ${page}:`, err);
            break;
          }
        }
      }

      setHasMore(false);
      setLastPage(page);
      setTotalRows(allDriversList.length);
      console.log(
        `Finished fetching all drivers. Total: ${allDriversList.length}`
      );
    } catch (err) {
      console.error('Error fetching all drivers:', err);
      setDrivers([]);
    } finally {
      setLoading(false);
      isFetchingRef.current = false;
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      await fetchShowrooms();
      await fetchAllDriversSequentially();
    };

    fetchData();
  }, []);

  // Refresh drivers after create/update/delete
  const refreshDrivers = () => {
    fetchAllDriversSequentially();
  };

  // Get all showroom names for a driver
  const getDriverShowroomNames = (driver) => {
    if (
      driver.showrooms &&
      Array.isArray(driver.showrooms) &&
      driver.showrooms.length > 0
    ) {
      return driver.showrooms.map((s) => s.name).join(', ');
    }

    // Fallback to showroom_id mapping if needed
    if (
      driver.showroom_id &&
      Array.isArray(driver.showroom_id) &&
      driver.showroom_id.length > 0
    ) {
      const names = driver.showroom_id
        .map((id) => {
          const showroom = showrooms.find((s) => s.id === id);
          return showroom ? showroom.name : null;
        })
        .filter(Boolean);
      return names.length > 0 ? names.join(', ') : 'Not Assigned';
    }

    return 'Not Assigned';
  };

  const filteredDrivers = useMemo(() => {
    return drivers.filter(
      (driver) =>
        (driver.name?.toLowerCase() || '').includes(search.toLowerCase()) ||
        (driver.email?.toLowerCase() || '').includes(search.toLowerCase()) ||
        (driver.phone?.toLowerCase() || '').includes(search.toLowerCase()) ||
        getDriverShowroomNames(driver)
          .toLowerCase()
          .includes(search.toLowerCase())
    );
  }, [drivers, search, showrooms]);

  // Removed Intersection Observer - now fetching all pages sequentially

  const handleOpenCreateModal = () => {
    setEditingDriver(null);
    setShowModal(true);
  };

  const handleOpenEditModal = (driver) => {
    setEditingDriver(driver);
    setShowModal(true);
  };

  const handleDeleteDriver = async (driverId) => {
    if (window.confirm('Are you sure you want to delete this driver?')) {
      try {
        await authAxios.delete(`/users/${driverId}`);
        // Reset and reload drivers from the beginning
        setDrivers([]);
        setCurrentPage(1);
        setHasMore(true);
        refreshDrivers();
      } catch (err) {
        console.error('Failed to delete driver:', err.response?.data || err);
      }
    }
  };

  const handleExportToExcel = () => {
    if (!drivers || drivers.length === 0) {
      return;
    }
    const exportData = drivers.map((d, idx) => ({
      'S.No': idx + 1,
      Name: d.name,
      Email: d.email || 'N/A',
      Phone: d.phone,
      Status: d.is_active ? 'Active' : 'Inactive',
      'Showroom/Branch': getDriverShowroomNames(d),
    }));
    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Drivers');
    const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([buf], { type: 'application/octet-stream' });
    saveAs(blob, `drivers_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingDriver(null);
  };

  // Custom Loading Component for DataTable
  const CustomLoadingIndicator = (
    <div className='p-3 text-center'>
      <Spinner
        animation='border'
        role='status'
        className='me-2'
        variant='primary'
      />
      <span className='text-primary'>
        {currentPage && lastPage
          ? `Loading drivers... (Page ${currentPage} of ${lastPage})`
          : 'Loading drivers...'}
      </span>
    </div>
  );

  // Custom No Data Component for DataTable
  const CustomNoDataComponent = (
    <div className='p-4 text-center text-muted'>
      <FaBoxOpen className='mb-2' size={32} />
      <h5 className='mt-2'>No Drivers Found</h5>
      <p className='mb-0'>
        There are currently no drivers in this list or matching your search
        criteria.
      </p>
    </div>
  );

  const columns = [
    {
      name: '#',
      selector: (row, index) => index + 1,
      sortable: true,
      width: '3vw',
      compact: true,
      grow: 0,
    },
    {
      name: 'Name',
      selector: (row) => row.name,
      sortable: true,
      minWidth: '8vw',
      wrap: true,
      grow: 1,
    },
    {
      name: 'Email',
      selector: (row) => row.email || 'N/A',
      sortable: true,
      minWidth: '10vw',
      wrap: true,
      grow: 1,
    },
    {
      name: 'Phone',
      selector: (row) => row.phone,
      sortable: true,
      minWidth: '6vw',
      wrap: true,
      grow: 1,
    },
    {
      name: 'Status',
      selector: (row) => (row.is_active ? 'Active' : 'Inactive'),
      sortable: true,
      minWidth: '5vw',
      wrap: true,
      grow: 1,
    },
    {
      name: 'Showroom/Branch',
      selector: (row) => getDriverShowroomNames(row),
      sortable: true,
      minWidth: '10vw',
      wrap: true,
      grow: 2,
    },
    {
      name: 'Actions',
      center: true,
      cell: (row) => {
        const buttons = [
          <Button
            key='edit'
            size='sm'
            variant='warning'
            onClick={() => handleOpenEditModal(row)}
            title='Edit driver'
          >
            <FaEdit />
          </Button>,
          <Button
            key='delete'
            size='sm'
            variant='danger'
            onClick={() => handleDeleteDriver(row.id)}
            title='Delete driver'
          >
            <FaTrashAlt />
          </Button>,
        ];

        return (
          <div
            style={{
              display: 'flex',
              gap: '8px',
              justifyContent: 'center',
              alignItems: 'center',
              padding: '8px 0',
            }}
          >
            {buttons.map((button) =>
              React.cloneElement(button, {
                style: {
                  ...button.props.style,
                  marginTop: '4px',
                  marginBottom: '4px',
                },
              })
            )}
          </div>
        );
      },
      minWidth: '6vw',
      grow: 0,
    },
  ];

  return (
    <div className='border p-2 rounded' style={{ background: '#fff' }}>
      <Container fluid className='p-4'>
        <h3 style={{ textAlign: 'center' }}>Driver Management</h3>
        <div className='row justify-content-between align-items-center mt-3 mb-3'>
          <div className='col-md-9 col-lg-9 col-sm-12 align-items-center py-3 gap-2'>
            <Row className='g-2'>
              <Col xs='auto'>
                <InputGroup style={{ width: '300px' }}>
                  <InputGroup.Text>
                    <i className='fa fa-search'></i>
                  </InputGroup.Text>
                  <Form.Control
                    type='text'
                    placeholder='Search Drivers'
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </InputGroup>
              </Col>
            </Row>
          </div>
          <div className='col-md-3 col-lg-3 col-sm-12 text-end'>
            <Button
              size='sm'
              variant='primary'
              onClick={handleOpenCreateModal}
              style={{ marginRight: '2px' }}
            >
              <i className='fa fa-plus'></i> New Driver
            </Button>
            <Button size='sm' variant='success' onClick={handleExportToExcel}>
              <FaFileExcel /> Export to Excel
            </Button>
          </div>
        </div>

        <div style={{ maxHeight: '54vh', position: 'relative' }}>
          {/* Count Display */}
          {filteredDrivers.length > 0 && (
            <div
              className='text-end p-2'
              style={{
                backgroundColor: '#f8f9fa',
                borderBottom: '1px solid #dee2e6',
              }}
            >
              <small className='text-muted'>
                Showing <strong>{1}</strong> to{' '}
                <strong>{filteredDrivers.length}</strong> out of{' '}
                <strong>{totalRows}</strong> drivers
              </small>
            </div>
          )}
          <DataTable
            columns={columns}
            data={filteredDrivers}
            pagination={false}
            highlightOnHover
            striped
            dense
            persistTableHead
            fixedHeader
            fixedHeaderScrollHeight='50vh'
            noTableHead={false}
            customStyles={{
              table: {
                style: {
                  tableLayout: 'auto',
                  width: '100%',
                  maxWidth: '100%',
                },
              },
              tableWrapper: {
                style: {
                  width: '100%',
                  maxWidth: '100%',
                  overflowX: 'auto',
                },
              },
              headRow: {
                style: {
                  backgroundColor: '#f8f9fa',
                  borderBottomStyle: 'solid',
                  borderBottomWidth: '2px',
                  borderBottomColor: '#dee2e6',
                },
              },
              headCells: {
                style: {
                  fontSize: '14px',
                  fontWeight: '600',
                  paddingLeft: '8px',
                  paddingRight: '8px',
                },
              },
              cells: {
                style: {
                  wordBreak: 'break-word',
                  whiteSpace: 'normal',
                },
              },
            }}
            progressPending={loading}
            progressComponent={CustomLoadingIndicator}
            noDataComponent={CustomNoDataComponent}
          />
          {!loading && drivers.length > 0 && (
            <div className='text-center p-3 text-muted'>
              <small>All drivers loaded ({drivers.length} total)</small>
            </div>
          )}
        </div>

        <AdminDriverCreation
          show={showModal}
          onClose={handleCloseModal}
          onDriverCreated={async () => {
            // Reset and reload drivers from the beginning
            setDrivers([]);
            setCurrentPage(1);
            setHasMore(true);
            refreshDrivers();
          }}
          driverToEdit={editingDriver}
        />
      </Container>
    </div>
  );
};

export default AdminDrivers;
